import { useCallback, useEffect, useState, type ComponentType } from 'react'
import {
    getStats, listInvoices, listSubscriptions, getRevenue, getBalance, getExpenseTypes,
    listTodos, createTodo, setTodoDone,
    type Stats, type Invoice, type Subscription, type RevenueSeries, type BalanceSeries, type Granularity, type ExpenseType, type Todo,
} from '../api'
import { useTabs, useDataVersion } from '../layout/TabsContext'
import { BRANDS, brandName, formatMoney } from '../lookups'
import { checkForm } from '../forms/checkForm'
import Chart, { CHART_COLORS } from '../components/Chart'
import { DASHBOARD_LIST_SIZE, CHART_INCOME_COLOR, CHART_EXPENSE_COLOR, CHART_NET_COLOR, CHART_YEARS_BACK } from '../../shared/constants'
import { fill, DASHBOARD_WIDGETS_TEXTS as TX } from '../../shared/texts'

// Registry of the available dashboard widgets. Which ones a user may pick is gated
// by `module` (AuthContext.can(module,'read')); the body component fetches its own
// data from the already RBAC-protected endpoints. See pages/Dashboard.tsx for the
// per-user assembly (order, width, persistence in user.preferences.dashboard).

type WidgetConfig = Record<string, unknown>
export interface WidgetMeta {
    id: string
    title: string
    module: string
    Component: ComponentType<{ config?: WidgetConfig }>
    // Configurable widgets can be placed multiple times, each with its own config.
    configurable?: boolean
    defaultConfig?: WidgetConfig
    instanceTitle?: (config?: WidgetConfig) => string
    ConfigEditor?: ComponentType<{ config: WidgetConfig, onChange: (c: WidgetConfig) => void }>
}

// The one formatter (constants.ts): the record's currency, else the backoffice currency.
const money = (n: number, currency?: string) => formatMoney(n, currency)

// Shared small-state hook: load, expose loading/error so a failing widget degrades on its own
// without taking down the whole dashboard. `version` (sum of useDataVersion for what the widget
// shows) reloads it when that data changes elsewhere; the old figures stay up meanwhile.
function useWidgetData<T>(load: () => Promise<T>, version: number): { data: T | null, loading: boolean, error: string } {
    const [data, setData] = useState<T | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    useEffect(() => {
        let alive = true
        load()
            .then((d) => { if (alive) { setData(d); setError('') } })
            .catch((e) => { if (alive) setError(e?.response?.data?.error ?? e?.message ?? TX.loadError) })
            .finally(() => { if (alive) setLoading(false) })
        return () => { alive = false }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [version])
    return { data, loading, error }
}

// --- Kennzahlen (stat cards) — module: dashboard ---
function StatsWidget() {
    const { data: stats, loading, error } = useWidgetData<Stats>(getStats, useDataVersion('invoices') + useDataVersion('subscriptions'))
    if (error) return <div className="notice err">{error}</div>
    if (loading) return <p className="muted">{TX.loading}</p>
    return (
        <div className="cards" style={{ marginBottom: 0 }}>
            <div className="card"><div className="value">{stats?.invoicesTotal ?? '–'}</div><div className="label">{TX.stats.invoicesTotal}</div></div>
            <div className="card"><div className="value">{stats?.invoicesByStatus?.draft ?? '–'}</div><div className="label">{TX.stats.drafts}</div></div>
            <div className="card"><div className="value">{stats?.unsent ?? '–'}</div><div className="label">{TX.stats.unsent}</div></div>
            <div className="card"><div className="value">{stats?.invoicesByStatus?.sent ?? '–'}</div><div className="label">{TX.stats.sent}</div></div>
            <div className="card"><div className="value">{stats?.subscriptionsTotal ?? '–'}</div><div className="label">{TX.stats.subscriptions}</div></div>
        </div>
    )
}

// Compact invoice list shared by the two invoice widgets.
function InvoiceMiniList({ params, empty }: { params: Parameters<typeof listInvoices>[0], empty: string }) {
    const { openTab } = useTabs()
    const { data, loading, error } = useWidgetData(() => listInvoices(params), useDataVersion('invoices'))
    if (error) return <div className="notice err">{error}</div>
    if (loading) return <p className="muted">{TX.loading}</p>
    const rows = data?.rows ?? []
    if (rows.length === 0) return <p className="muted">{empty}</p>
    const openInvoices = () => openTab({ id: 'invoices', name: TX.invoicesTab, component: 'invoices' })
    return (
        <ul className="dash-list">
            {rows.map((inv: Invoice) => (
                <li key={inv._id} className="dash-list-row" onClick={openInvoices} title={TX.toInvoices}>
                    <span className="dash-list-main">
                        {inv.invoiceNumber ?? <span className="muted">{TX.draft}</span>}
                        <span className="muted"> · {inv.billing?.company || inv.billing?.displayName || '—'}</span>
                    </span>
                    <span className="dash-list-meta">{money(inv.totals?.gross ?? 0, inv.currency)}</span>
                </li>
            ))}
        </ul>
    )
}

// --- Unversendete Rechnungen (finalized but not sent) — module: invoices ---
function UnsentInvoicesWidget() {
    return <InvoiceMiniList
        params={{ status: 'finalized', sort: 'createdAt', dir: 'desc', pageSize: DASHBOARD_LIST_SIZE }}
        empty={TX.noUnsentInvoices} />
}

// --- Letzte Rechnungen — module: invoices ---
function RecentInvoicesWidget() {
    return <InvoiceMiniList
        params={{ sort: 'createdAt', dir: 'desc', pageSize: DASHBOARD_LIST_SIZE }}
        empty={TX.noInvoicesYet} />
}

// --- Fällige Abos (active, soonest next run first) — module: subscriptions ---
function DueSubscriptionsWidget() {
    const { openTab } = useTabs()
    const { data, loading, error } = useWidgetData(() =>
        listSubscriptions({ status: 'active', sort: 'nextRunAt', dir: 'asc', pageSize: DASHBOARD_LIST_SIZE }),
        useDataVersion('subscriptions') + useDataVersion('customers'))
    if (error) return <div className="notice err">{error}</div>
    if (loading) return <p className="muted">{TX.loading}</p>
    const rows = data?.rows ?? []
    if (rows.length === 0) return <p className="muted">{TX.noDueSubscriptions}</p>
    const openEdit = (s: Subscription) => openTab({
        id: `subscription-${s._id}`,
        name: fill(TX.subscriptionTab, { name: s.customerName || s.customerId }),
        component: 'subscription-form',
        config: { id: s._id },
    })
    return (
        <ul className="dash-list">
            {rows.map((s: Subscription) => (
                <li key={s._id} className="dash-list-row" onClick={() => openEdit(s)} title={TX.openSubscription}>
                    <span className="dash-list-main">
                        {s.customerName || s.customerId}
                        <span className="muted"> · {brandName(s.brand)}</span>
                    </span>
                    <span className="dash-list-meta">
                        {s.nextRunAt ? new Date(s.nextRunAt).toLocaleDateString('de-DE') : '—'}
                    </span>
                </li>
            ))}
        </ul>
    )
}

// --- Umsatz-Chart (configurable, multi-instance) — module: dashboard ---
const MONTHS_SHORT = TX.monthsShort
type Preset = 'week' | 'month' | 'year' | 'last-year' | 'years-3-overlay'
const PRESETS: { key: Preset, label: string }[] = [
    { key: 'week', label: TX.presets.thisWeek },
    { key: 'month', label: TX.presets.thisMonth },
    { key: 'year', label: TX.presets.thisYear },
    { key: 'last-year', label: TX.presets.lastYear },
    { key: 'years-3-overlay', label: fill(TX.presets.lastYears, { n: CHART_YEARS_BACK + 1 }) },
]
const presetLabel = (p?: unknown) => PRESETS.find((x) => x.key === p)?.label ?? TX.presets.thisYear

// A preset → the API query range (server buckets by its local time, like report.ts).
function presetRange(preset: Preset): { granularity: Granularity, from: Date, to: Date } {
    const now = new Date(), y = now.getFullYear(), m = now.getMonth()
    if (preset === 'week') {
        const from = new Date(now); from.setHours(0, 0, 0, 0)
        from.setDate(now.getDate() - ((now.getDay() + 6) % 7)) // back to Monday
        const to = new Date(from); to.setDate(from.getDate() + 7)
        return { granularity: 'day', from, to }
    }
    if (preset === 'month') return { granularity: 'day', from: new Date(y, m, 1), to: new Date(y, m + 1, 1) }
    if (preset === 'last-year') return { granularity: 'month', from: new Date(y - 1, 0, 1), to: new Date(y, 0, 1) }
    if (preset === 'years-3-overlay') return { granularity: 'month', from: new Date(y - CHART_YEARS_BACK, 0, 1), to: new Date(y + 1, 0, 1) }
    return { granularity: 'month', from: new Date(y, 0, 1), to: new Date(y + 1, 0, 1) }
}

// Local calendar date YYYY-MM-DD (the API interprets the range in server-local time).
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

function bucketLabel(bucket: string, g: Granularity): string {
    if (g === 'month') return MONTHS_SHORT[Number(bucket.slice(5, 7)) - 1] ?? bucket
    if (g === 'day') return bucket.slice(8, 10)
    return bucket
}

// Stacked bars: one series per brand (gross).
function barData(s: RevenueSeries) {
    return {
        labels: s.buckets.map((b) => bucketLabel(b.bucket, s.granularity)),
        series: BRANDS.map((br, i) => ({
            name: brandName(br.key),
            color: CHART_COLORS[i % CHART_COLORS.length],
            values: s.buckets.map((b) => b.byBrand[br.key]?.gross ?? 0),
        })),
    }
}
// Overlay lines: pivot 36 monthly buckets into one series per year (Jan–Dez x-axis).
function overlayData(s: RevenueSeries) {
    const years = [...new Set(s.buckets.map((b) => Number(b.bucket.slice(0, 4))))].sort()
    const byKey = new Map(s.buckets.map((b) => [b.bucket, b]))
    return {
        labels: [...MONTHS_SHORT],
        series: years.map((Y, i) => ({
            name: String(Y),
            color: CHART_COLORS[i % CHART_COLORS.length],
            values: Array.from({ length: 12 }, (_, mi) => byKey.get(`${Y}-${String(mi + 1).padStart(2, '0')}`)?.gross ?? 0),
        })),
    }
}

function RevenueChartWidget({ config }: { config?: WidgetConfig }) {
    const preset = ((config?.preset as Preset) ?? 'year')
    const [data, setData] = useState<RevenueSeries | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const version = useDataVersion('invoices')
    // No „lädt…" on a reload: the previous chart stays up until the new figures are in.
    useEffect(() => {
        let alive = true
        setError('')
        const r = presetRange(preset)
        getRevenue({ granularity: r.granularity, from: ymd(r.from), to: ymd(r.to) })
            .then((d) => { if (alive) setData(d) })
            .catch((e) => { if (alive) setError(e?.response?.data?.error ?? e?.message ?? TX.loadError) })
            .finally(() => { if (alive) setLoading(false) })
        return () => { alive = false }
    }, [preset, version])
    if (error) return <div className="notice err">{error}</div>
    if (loading || !data) return <p className="muted">{TX.loading}</p>
    const money = (n: number) => formatMoney(n, data.currency, { whole: true })
    const overlay = preset === 'years-3-overlay'
    const shaped = overlay ? overlayData(data) : barData(data)
    return (
        <div>
            <div className="chart-total">{money(data.totals.gross)} <span className="muted">{TX.grossTotal}</span></div>
            <Chart mode={overlay ? 'lines' : 'stacked-bars'} labels={shaped.labels} series={shaped.series} formatValue={money} height={150} />
        </div>
    )
}

function RevenueChartConfig({ config, onChange }: { config: WidgetConfig, onChange: (c: WidgetConfig) => void }) {
    const preset = (config.preset as Preset) ?? 'year'
    return (
        <select value={preset} onChange={(e) => onChange({ ...config, preset: e.target.value })}>
            {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
    )
}

// --- Bilanz (Einnahmen/Ausgaben/Saldo) — module: dashboard ---
type BalPreset = 'year' | 'last-year' | 'years-3'
const BAL_PRESETS: { key: BalPreset, label: string }[] = [
    { key: 'year', label: TX.presets.thisYear },
    { key: 'last-year', label: TX.presets.lastYear },
    { key: 'years-3', label: fill(TX.presets.lastYears, { n: CHART_YEARS_BACK + 1 }) },
]
const balLabel = (p?: unknown) => BAL_PRESETS.find((x) => x.key === p)?.label ?? TX.presets.thisYear

function balRange(preset: BalPreset): { granularity: 'month' | 'year', from: Date, to: Date } {
    const y = new Date().getFullYear()
    if (preset === 'last-year') return { granularity: 'month', from: new Date(y - 1, 0, 1), to: new Date(y, 0, 1) }
    if (preset === 'years-3') return { granularity: 'year', from: new Date(y - CHART_YEARS_BACK, 0, 1), to: new Date(y + 1, 0, 1) }
    return { granularity: 'month', from: new Date(y, 0, 1), to: new Date(y + 1, 0, 1) }
}

function BalanceChartWidget({ config }: { config?: WidgetConfig }) {
    const preset = ((config?.preset as BalPreset) ?? 'year')
    const [data, setData] = useState<BalanceSeries | null>(null)
    const [types, setTypes] = useState<ExpenseType[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    useEffect(() => { getExpenseTypes().then(setTypes).catch(() => {}) }, [])
    // Income from the invoices, expenses from the filed vendor invoices ('documents').
    const version = useDataVersion('invoices') + useDataVersion('documents')
    useEffect(() => {
        let alive = true
        setError('')
        const r = balRange(preset)
        getBalance({ granularity: r.granularity, from: ymd(r.from), to: ymd(r.to) })
            .then((d) => { if (alive) setData(d) })
            .catch((e) => { if (alive) setError(e?.response?.data?.error ?? e?.message ?? TX.loadError) })
            .finally(() => { if (alive) setLoading(false) })
        return () => { alive = false }
    }, [preset, version])
    if (error) return <div className="notice err">{error}</div>
    if (loading || !data) return <p className="muted">{TX.loading}</p>
    const money = (n: number) => formatMoney(n, data.currency, { whole: true })
    const labels = data.buckets.map((b) => bucketLabel(b.bucket, data.granularity))
    const series = [
        { name: TX.income, color: CHART_INCOME_COLOR, values: data.buckets.map((b) => b.income) },
        { name: TX.expense, color: CHART_EXPENSE_COLOR, values: data.buckets.map((b) => b.expense) },
        { name: TX.net, color: CHART_NET_COLOR, values: data.buckets.map((b) => b.net) },
    ]
    const typeLabel = (key: string) => key ? (types.find((t) => t.key === key)?.label ?? key) : TX.noType
    const byType = Object.entries(data.totals.expenseByType ?? {})
        .filter(([, v]) => v)
        .sort((a, b) => b[1] - a[1])
    return (
        <div>
            <div className="chart-total">{money(data.totals.net)} <span className="muted">{fill(TX.balanceLine, { income: money(data.totals.income), expense: money(data.totals.expense) })}</span></div>
            <Chart mode="lines" labels={labels} series={series} formatValue={money} height={150}
                aside={byType.length > 0 ? (
                    <div className="dash-list">
                        <div className="muted" style={{ marginBottom: 4 }}>{TX.expenseByType}</div>
                        {byType.map(([key, val]) => (
                            <div key={key || '_'} className="dash-list-row">
                                <span>{typeLabel(key)}</span>
                                <span className="dash-list-meta">{money(val)}</span>
                            </div>
                        ))}
                    </div>
                ) : undefined} />
        </div>
    )
}

function BalanceChartConfig({ config, onChange }: { config: WidgetConfig, onChange: (c: WidgetConfig) => void }) {
    const preset = (config.preset as BalPreset) ?? 'year'
    return (
        <select value={preset} onChange={(e) => onChange({ ...config, preset: e.target.value })}>
            {BAL_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
    )
}


// --- Arbeitsliste (Todos) — module: todos ---
//
// Betreiber, 11.09.2026: „Die Liste wird dann angezeigt und muss per Toggler als erledigt
// markiert werden. Der Punkt wird dann nicht mehr angezeigt."
//
// ⛔ Abgehakt heisst AUSGEBLENDET, nicht geloescht — der Datensatz bleibt mit seinem Zeitpunkt
// stehen. Das Entfernen ist ein Schritt des Betreibers, so fuehrt er seine `todo.md` seit jeher.
//
// ⚠️ Dieses Widget laedt selbst nach, wenn woanders etwas passiert (`useDataVersion('todos')`).
// Der Realtime-Kanal benachrichtigt aber nur die ANDEREN Fenster, nie den Absender — wer in
// einem Fenster abhakt und im selben Fenster auf eine Aenderung wartet, wartet vergeblich.
// Deshalb aktualisiert der Toggle unten die eigene Liste direkt und meldet die Aenderung
// zusaetzlich nach draussen.
function TodosWidget() {
    const { publishChange } = useTabs()
    const fremdVersion = useDataVersion('todos')
    const [rows, setRows] = useState<Todo[] | null>(null)
    const [fehler, setFehler] = useState('')
    const [neu, setNeu] = useState('')
    const [busy, setBusy] = useState(false)

    const laden = useCallback(() => {
        listTodos()
            .then(setRows)
            .catch((e) => setFehler(e?.response?.data?.error ?? e?.message ?? TX.loadError))
    }, [])
    useEffect(() => { laden() }, [laden, fremdVersion])

    async function anlegen(e: React.FormEvent) {
        e.preventDefault()
        if (!checkForm(e.currentTarget as HTMLFormElement).ok) return
        const text = neu.trim()
        if (!text || busy) return
        setBusy(true)
        try {
            await createTodo(text)
            setNeu('')
            laden()
            publishChange('todos')
        } catch (err: any) {
            setFehler(err?.response?.data?.error ?? TX.todos.createFailed)
        } finally { setBusy(false) }
    }

    async function abhaken(t: Todo) {
        // Sofort aus der Anzeige nehmen, dann schreiben: Das Abhaken ist die Geste, auf die es
        // hier ankommt, und sie soll sich nicht nach Netz anfuehlen. Schlaegt es fehl, kommt der
        // Punkt zurueck — mit Grund.
        setRows((alt) => (alt ?? []).filter((x) => x._id !== t._id))
        try {
            await setTodoDone(t._id, true)
            publishChange('todos')
        } catch (err: any) {
            setFehler(err?.response?.data?.error ?? TX.todos.doneFailed)
            laden()
        }
    }

    return (
        <div>
            {fehler && <div className="notice err" style={{ marginBottom: 8 }}>{fehler}</div>}

            {rows === null ? <p className="muted">{TX.loading}</p> : rows.length === 0 ? (
                // ⚠️ Der Normalfall am ersten Tag. Eine leere Flaeche liesse den Betrachter
                // raten, ob das Modul kaputt ist oder nichts ansteht.
                <p className="muted" style={{ margin: '4px 0 10px' }}>{TX.todos.nothingOpen}</p>
            ) : (
                <ul className="dash-list todo-list-scroll">
                    {rows.map((t) => (
                        // Dieselbe Zeilenform wie die Nachbar-Widgets (`dash-list-row`), nicht
                        // eine eigene daneben: Haupttext links, Meta rechts. Was fehlt, sind
                        // genau zwei Dinge — die Checkbox und dass hier NICHT die ganze Zeile
                        // klickt, sondern nur das Label.
                        <li key={t._id} className="dash-list-row todo-row">
                            <label className="dash-list-main todo-label">
                                {/* Immer ungehakt: Was hier steht, ist offen. Ein Haken bedeutet
                                    „erledigen", nicht „erledigt" — der Punkt verschwindet danach. */}
                                <input type="checkbox" checked={false} onChange={() => void abhaken(t)}
                                    aria-label={fill(TX.todos.markDoneAria, { text: t.text })} />
                                <span>{t.text}</span>
                            </label>
                            {/* Nur das Datum, nicht die Uhrzeit: Es beantwortet „wie lange liegt
                                das schon?", und dafuer ist die Minute ohne Belang — sie steht im
                                `title`, falls doch jemand fragt. */}
                            <time className="dash-list-meta" dateTime={t.createdAt}
                                title={new Date(t.createdAt).toLocaleString('de-DE')}>
                                {new Date(t.createdAt).toLocaleDateString('de-DE')}
                            </time>
                        </li>
                    ))}
                </ul>
            )}

            <form onSubmit={anlegen} noValidate className="todo-add">
                <input value={neu} onChange={(e) => setNeu(e.target.value)} required
                    placeholder={TX.todos.newPlaceholder} aria-label={TX.todos.newAria} />
                <button type="submit" disabled={busy}>{TX.todos.add}</button>
            </form>
        </div>
    )
}

export const WIDGETS: WidgetMeta[] = [
    { id: 'stats', title: TX.widgetTitles.stats, module: 'dashboard', Component: StatsWidget },
    { id: 'todos', title: TX.widgetTitles.todos, module: 'todos', Component: TodosWidget },
    { id: 'unsent', title: TX.widgetTitles.unsent, module: 'invoices', Component: UnsentInvoicesWidget },
    { id: 'recent', title: TX.widgetTitles.recent, module: 'invoices', Component: RecentInvoicesWidget },
    { id: 'dueSubs', title: TX.widgetTitles.dueSubs, module: 'subscriptions', Component: DueSubscriptionsWidget },
    {
        id: 'revenue-chart', title: TX.widgetTitles.revenueChart, module: 'revenue', Component: RevenueChartWidget,
        configurable: true, defaultConfig: { preset: 'year' },
        instanceTitle: (c) => fill(TX.revenueInstance, { preset: presetLabel(c?.preset) }),
        ConfigEditor: RevenueChartConfig,
    },
    {
        id: 'balance-chart', title: TX.widgetTitles.balanceChart, module: 'balance', Component: BalanceChartWidget,
        configurable: true, defaultConfig: { preset: 'year' },
        instanceTitle: (c) => fill(TX.balanceInstance, { preset: balLabel(c?.preset) }),
        ConfigEditor: BalanceChartConfig,
    },
]

export const WIDGET_BY_ID: Record<string, WidgetMeta> = Object.fromEntries(WIDGETS.map((w) => [w.id, w]))
